import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Modal, Form, Spinner } from "react-bootstrap"
import swal from "sweetalert"
import Select from "react-select"
import {
  getServiceCallerListAction,
  getPatientsListAction,
  reassignPatientsAction,
} from "../../../../store/actions/ServiceCallerReassignmentAction"

const ServiceCallerReassignment = () => {
  const dispatch = useDispatch()

  // Get data from Redux store
  const { serviceCallerList, patientsList, loading: reduxLoading } = useSelector(
    (state) => state.serviceCallerReassignment || {}
  )

  // State management
  const [selectedServiceCallerId, setSelectedServiceCallerId] = useState(null)
  const [selectedServiceCallerName, setSelectedServiceCallerName] = useState("")
  const [localPatientsList, setLocalPatientsList] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedNewServiceCallerId, setSelectedNewServiceCallerId] = useState(null)
  const [searchServiceCaller, setSearchServiceCaller] = useState("")
  const [reassigning, setReassigning] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)

  // Search, Sort, and Pagination state
  const [searchQueries, setSearchQueries] = useState({
    name: "",
    email: "",
    diseases: "",
  })
  const [sortField, setSortField] = useState("name")
  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10

  const loading = localLoading || reduxLoading

  // Load service callers dropdown on page load
  useEffect(() => {
    loadServiceCallers()
  }, [])

  // Load patients when service caller is selected
  useEffect(() => {
    if (selectedServiceCallerId) {
      // Clear old data immediately before loading new data
      setLocalPatientsList([])
      setSelectAll(false)
      setCurrentPage(0)
      setSearchQueries({ name: "", email: "", diseases: "" })
      loadPatients()
    } else {
      setLocalPatientsList([])
      setSelectAll(false)
    }
  }, [selectedServiceCallerId])

  // Update local patients list when Redux patientsList changes
  useEffect(() => {
    if (patientsList) {
      if (patientsList.length > 0) {
        const formatted = patientsList.map(patient => ({
          id: patient.patient_id,
          name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
          email: patient.patient_email,
          diseases: patient.diseases?.map(d => d.disease_name).join(", ") || "-",
          selected: false,
        }))
        setLocalPatientsList(formatted)
        setSelectAll(false)
        setCurrentPage(0)
      } else {
        // Empty response - show empty state
        setLocalPatientsList([])
      }
    }
  }, [patientsList])

  const loadServiceCallers = async () => {
    setLocalLoading(true)
    await dispatch(getServiceCallerListAction())
    setLocalLoading(false)
  }

  const loadPatients = async () => {
    setLocalLoading(true)
    await dispatch(getPatientsListAction(selectedServiceCallerId))
    setLocalLoading(false)
  }

  // Apply filters and sorting
  const filteredData = localPatientsList
    .filter((patient) =>
      Object.keys(searchQueries).every((key) =>
        patient[key]?.toString().toLowerCase().includes(searchQueries[key].toLowerCase())
      )
    )
    .sort((a, b) => {
      const modifier = sortOrder === "asc" ? 1 : -1
      if (a[sortField] < b[sortField]) return -1 * modifier
      if (a[sortField] > b[sortField]) return 1 * modifier
      return 0
    })

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage)
  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  )

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page)
    }
  }

  const handleSearch = (field, value) => {
    setSearchQueries(prev => ({
      ...prev,
      [field]: value
    }))
    setCurrentPage(0)
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortOrder("asc")
    }
  }

  const handleSelectAll = () => {
    const newState = !selectAll
    setSelectAll(newState)
    setLocalPatientsList(localPatientsList.map(p => ({ ...p, selected: newState })))
  }

  const handleSelectPatient = (id) => {
    const updated = localPatientsList.map(p => 
      p.id === id ? { ...p, selected: !p.selected } : p
    )
    setLocalPatientsList(updated)
    setSelectAll(updated.every(p => p.selected))
  }

  const handleReassign = async () => {
    if (!selectedNewServiceCallerId) {
      swal("Error", "Select a service caller", "error")
      return
    }

    const selectedIds = localPatientsList.filter(p => p.selected).map(p => p.id)
    if (selectedIds.length === 0) {
      swal("Error", "No patients selected", "error")
      return
    }

    setReassigning(true)
    const result = await dispatch(reassignPatientsAction(selectedServiceCallerId, selectedIds, selectedNewServiceCallerId))
    if (result?.success) {
      // swal("Success", `${selectedIds.length} patients reassigned successfully!`, "success")
      handleCloseModal()
      loadPatients()
    }
    setReassigning(false)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSearchServiceCaller("")
    setSelectedNewServiceCallerId(null)
  }

  const dropdownOptions = (serviceCallerList || []).map(caller => ({ 
    value: caller.admin_id, 
    label: caller.admin_name 
  }))

  const modalOptions = (serviceCallerList || [])
    .filter(caller => caller.admin_id !== selectedServiceCallerId)
    .map(caller => ({ 
      value: caller.admin_id, 
      label: caller.admin_name 
    }))

  const selectedCount = localPatientsList.filter(p => p.selected).length

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      height: "38px",
      borderRadius: "6px",
      borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 1px #0d6efd" : "none",
      "&:hover": {
        borderColor: "#0d6efd",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "38px",
      padding: "0 8px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
        ? "#e9f2ff"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      cursor: "pointer",
    }),
  }

  return (
    <>
      <div className="d-flex justify-content-center mb-3">
        <div style={{ width: "300px" }}>
          <Select 
            options={dropdownOptions}
            styles={customSelectStyles}
            placeholder="Select Service Caller"
            onChange={(opt) => {
              setSelectedServiceCallerId(opt?.value)
              setSelectedServiceCallerName(opt?.label)
            }}
            isClearable
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : !selectedServiceCallerId ? (
            <div className="text-center py-5">
              <i className="fa fa-users fa-3x text-muted"></i>
              <p className="mt-3 text-muted">Select a service caller to view patients</p>
            </div>
          ) : (
            <>
              {/* Reassign Button */}
              {selectedCount > 0 && (
                <div className="mb-3 d-flex justify-content-end">
                  <Button variant="outline-primary" onClick={() => setShowModal(true)}>
                    Reassign To 
                  </Button>
                </div>
              )}

              {/* Patient Table */}
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th>
                        <Form.Check 
                          type="checkbox" 
                          checked={selectAll} 
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                        Name {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Search Name"
                          value={searchQueries.name}
                          onChange={(e) => handleSearch("name", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
                        Email {sortField === "email" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Search Email"
                          value={searchQueries.email}
                          onChange={(e) => handleSearch("email", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th onClick={() => handleSort("diseases")} style={{ cursor: "pointer" }}>
                        Diseases {sortField === "diseases" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          placeholder="Search Diseases"
                          value={searchQueries.diseases}
                          onChange={(e) => handleSearch("diseases", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map(p => (
                      <tr key={p.id}>
                        <td>
                          <Form.Check 
                            type="checkbox" 
                            checked={p.selected} 
                            onChange={() => handleSelectPatient(p.id)}
                          />
                        </td>
                        <td>{p.name}</td>
                        <td>{p.email}</td>
                        <td>{p.diseases}</td>
                      </tr>
                    ))}
                    {paginatedData.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          No patients found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredData.length > 0 && (
                <div
                  className="pagination-wrapper"
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "20px",
                    flexWrap: "wrap",
                    flexDirection: "column",
                  }}
                >
                  <div>
                    Showing {paginatedData?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                    {Math.min((currentPage + 1) * rowsPerPage, filteredData.length)} of {filteredData.length} entries
                  </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                  <button
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                    disabled={currentPage === 0}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === 0 ? "not-allowed" : "pointer",
                      opacity: currentPage === 0 ? 0.5 : 1,
                    }}
                  >
                    &lt;
                  </button>
                  {(() => {
                    const pages = []
                    const maxPagesToShow = 5
                    const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow))
                    const endPage = Math.min(startPage + maxPagesToShow, totalPages)

                    if (startPage > 0) {
                      pages.push(
                        <button
                          key={0}
                          onClick={() => handlePageChange(0)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          1
                        </button>
                      )
                      if (startPage > 1) {
                        pages.push(
                          <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>
                        )
                      }
                    }

                    for (let i = startPage; i < endPage; i++) {
                      const isActive = currentPage === i
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: isActive ? "#007bff" : "#fff",
                            color: isActive ? "#fff" : "#000",
                            cursor: "pointer",
                          }}
                        >
                          {i + 1}
                        </button>
                      )
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>
                        )
                      }
                      pages.push(
                        <button
                          key={totalPages - 1}
                          onClick={() => handlePageChange(totalPages - 1)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          {totalPages}
                        </button>
                      )
                    }

                    return pages
                  })()}
                  <button
                    onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                    disabled={currentPage === totalPages - 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                      opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    }}
                  >
                    &gt;
                  </button>
                </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Reassign Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Reassign to Service Caller</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Search Service Caller</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by name"
              value={searchServiceCaller}
              onChange={(e) => setSearchServiceCaller(e.target.value)}
            />
          </Form.Group>
          <div className="service-caller-list">
            {serviceCallerList && serviceCallerList.filter(caller => 
              caller.admin_id !== selectedServiceCallerId &&
              caller.admin_name.toLowerCase().includes(searchServiceCaller.toLowerCase())
            ).length > 0 ? (
              serviceCallerList
                .filter(caller => 
                  caller.admin_id !== selectedServiceCallerId &&
                  caller.admin_name.toLowerCase().includes(searchServiceCaller.toLowerCase())
                )
                .map((caller) => (
                  <div key={caller.admin_id} className="service-caller-item p-2">
                    <Form.Check
                      type="radio"
                      id={`service-caller-${caller.admin_id}`}
                      name="serviceCallerReassign"
                      label={
                        <div>
                          <div className="fw-bold">{caller.admin_name}</div>
                          <div className="fw-bold">{caller.admin_mobile}</div>
                        </div>
                      }
                      checked={selectedNewServiceCallerId === caller.admin_id}
                      onChange={() => setSelectedNewServiceCallerId(caller.admin_id)}
                      className="d-flex align-items-start gap-2"
                    />
                  </div>
                ))
            ) : (
              <p className="text-center text-muted">No service callers found</p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleReassign} 
            disabled={reassigning || !selectedNewServiceCallerId}
          >
            {reassigning ? <Spinner size="sm" /> : "Reassign"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ServiceCallerReassignment
